import {
  ROUTE_AUDIT_DETAIL,
  ROUTE_PERFORM_AUDIT,
  ROUTE_TASK_DETAIL,
  ROUTE_TASK_DETAIL_DESKTOP,
} from '../../routes';

/**
 * v2 detail-page URL builders.
 *
 * Every detail route in v2 (`/audits/details`, `/audits/perform`,
 * `/tasks/details`) is keyed by BOTH the entity id AND the business
 * unit — a bare id is not unique across BUs. These helpers are the
 * single source of truth for constructing those URLs so a call site
 * can't accidentally omit `businessUnitId` from the query string.
 *
 * The output shape uses `URLSearchParams`, so:
 *   - values are URL-encoded automatically
 *   - order stays predictable across builds (the underlying `URLSearchParams`
 *     preserves insertion order)
 *   - callers stay declarative — no string concatenation.
 *
 * Reconciliation (auto-switching the user's selected BU when the URL's
 * BU differs) is handled downstream by `useReconcileBusinessUnitFromUrl`;
 * these builders just make sure the param is always present.
 */

/**
 * Build the audit-detail path.
 *
 * @param businessUnitId - Business unit the audit lives under.
 * @param auditId - Audit id.
 * @param extraParams - Optional extra query params (e.g. `{ closed: 'true' }`
 *   for the post-close success flash). Values are appended after the two
 *   required params; keys already present would be overwritten but the
 *   caller shouldn't do that.
 */
export function buildAuditDetailPath(
  businessUnitId: string,
  auditId: string,
  extraParams?: Readonly<Record<string, string>>,
): string {
  const params = new URLSearchParams({ auditId, businessUnitId });
  if (extraParams) {
    for (const [key, value] of Object.entries(extraParams)) {
      params.set(key, value);
    }
  }
  return `${ROUTE_AUDIT_DETAIL}?${params.toString()}`;
}

/**
 * Build the task-detail path.
 *
 * @param businessUnitId - Business unit the task lives under.
 * @param taskId - Task id.
 */
export function buildTaskDetailPath(businessUnitId: string, taskId: string): string {
  const params = new URLSearchParams({ taskId, businessUnitId });
  return `${ROUTE_TASK_DETAIL}?${params.toString()}`;
}

/**
 * Build the task-detail path the legacy desktop router serves.
 *
 * Same query string as {@link buildTaskDetailPath}, different path. Desktop and v2
 * disagree on this one route, so a v2 component rendered inside a legacy desktop page has
 * to build the desktop path or the link resolves to nothing.
 *
 * @param businessUnitId - Business unit the task lives under.
 * @param taskId - Task id.
 */
export function buildDesktopTaskDetailPath(
  businessUnitId: string,
  taskId: string,
): string {
  const params = new URLSearchParams({ taskId, businessUnitId });
  return `${ROUTE_TASK_DETAIL_DESKTOP}?${params.toString()}`;
}

/** Options for the perform-audit URL. */
export interface PerformAuditPathOptions {
  /**
   * `'view'` renders the perform-audit surface read-only (completed /
   * expired audits). Any other value — or the caller passing `'edit'`
   * explicitly — puts the page in edit mode; audit-level status still
   * gates whether the buttons are actually enabled.
   */
  readonly mode: 'view' | 'edit';
  /**
   * Optional deep-link target — the question the perform-audit page
   * should scroll to / open on mount. Comes from the audit-detail
   * question-nav taps.
   */
  readonly questionId?: string;
  /**
   * Optional sub-question deep-link. Must be paired with a
   * `questionId` — a sub-question is meaningless without its parent.
   * Ignored when `questionId` is absent.
   */
  readonly subQuestionId?: string;
}

/**
 * Build the perform-audit path.
 *
 * `mode` is always emitted (the perform-audit page treats a missing
 * `mode` as edit, but we still write it explicitly so bookmarks are
 * unambiguous). `questionId` and `subQuestionId` are only emitted
 * when set — they're deep-link hints, not required.
 */
export function buildPerformAuditPath(
  businessUnitId: string,
  auditId: string,
  options: PerformAuditPathOptions,
): string {
  const params = new URLSearchParams({
    auditId,
    businessUnitId,
    mode: options.mode,
  });
  if (options.questionId) {
    params.set('questionId', options.questionId);
    if (options.subQuestionId) {
      params.set('subQuestionId', options.subQuestionId);
    }
  }
  return `${ROUTE_PERFORM_AUDIT}?${params.toString()}`;
}
